import * as THREE from 'three/webgpu';
import { uniform as tslUniform } from 'three/tsl';

/**
 * Where the viewer is, in world space, as a plain uniform.
 *
 * **Use this instead of the TSL `cameraPosition` built-in in anything that can
 * render in XR.** On three r185 `cameraPosition` has two branches (see
 * `three/src/nodes/accessors/Camera.js`): a plain uniform when the camera is
 * ordinary, and — the moment the camera is an `ArrayCamera`, which is every XR
 * frame — a `uniformArray` indexed per view. That array branch attaches
 * `onRenderUpdate` to the `uniformArray`, shadowing `UniformArrayNode.prototype
 * .update`, and `setup()` then calls it with no frame. The graph throws at
 * shader-build time and the object silently disappears: no error surfaces, the
 * draw just never lands.
 *
 * That single footgun is why tree billboards vanished on a Quest while looking
 * fine on a desktop preview, and why the bunker and cart-path edge blends came
 * out wrong in the same session — every one of them measured a distance from
 * `cameraPosition`. `BallTrail` hit it first and worked around it locally with
 * its own `uViewer`; this is that fix generalised so no shader has to rediscover
 * it.
 *
 * Deliberately ONE position for both eyes, not a per-eye one. A per-eye value
 * puts distance-fade and blend edges in a different place in each eye, which
 * reads as binocular rivalry rather than depth. MR pages feed the eye MIDPOINT.
 */
export const viewerPosition = tslUniform(new THREE.Vector3(0, 1.6, 0));

let explicit = false;

/**
 * Feed the viewer position. In XR pass the eye midpoint, in reference space —
 * the same frame `positionWorld` resolves to, since the scene root is identity.
 *
 * Calling this once opts the app out of the camera fallback below, permanently:
 * an app that knows where the viewer is always knows better than the render
 * camera does, and in XR the render camera is the thing we cannot trust.
 */
export function setViewerPosition(position: THREE.Vector3) {
  explicit = true;
  viewerPosition.value.copy(position);
}

/**
 * Fallback for pages that never call `setViewerPosition` — the flat-screen
 * course and range, where there is no head to take a midpoint of. Called from
 * `FuseRenderer.render`, so those pages keep working with no changes.
 */
export function syncViewerFromCamera(camera: THREE.Camera) {
  if (explicit) return;
  viewerPosition.value.setFromMatrixPosition(camera.matrixWorld);
}
